import { useCallback, useEffect, useState } from "react";
import { Info } from "lucide-react";
import { theme, btnPrimary, btnSecondary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { toDateInput, dateInputToIso, fmtDateOnly } from "../lib/dateOnly";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PortalLink } from "../components/PortalLink";
import { frameworkAccent } from "../components/controlStatus";
import { StandardHeader, StandardNote, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { ControlGroup, ControlGroupStack } from "../components/standards/ControlGroup";
import { ReferenceRow } from "../components/standards/ControlRow";
import { SummaryBar } from "../components/standards/StatusSummary";
import { MaturityPicker, MaturityPill, type MaturityLevel } from "../components/standards/MaturityScale";

// NIST CSF 2.0 is a guidance framework rather than a certifiable standard, so there is no SoA or
// certification workflow. The six functions sit in the same collapsible groups as every other
// standard's clauses, each with its self-assessed implementation tier and its categories as
// reference rows; the organisation's compliance check schedule for CSF is summarised above.

// Implementation tiers in the framework's own words — the default scale, shown until
// GET /maturity/nist_csf returns the scale in force (backend lib/maturityScales.ts), which
// may be the CMMI 0–5 process maturity scale instead. Keep the two identical.
const MATURITY_TIERS: MaturityLevel[] = [
  { value: 1, label: "Partial" },
  { value: 2, label: "Risk Informed" },
  { value: 3, label: "Repeatable" },
  { value: 4, label: "Adaptive" },
];

// The tiers used to live only in this browser's localStorage. They are now per organisation
// (PUT /maturity/nist_csf/:itemId). The key is still read once, to migrate whatever a browser
// still holds, and then cleared — see migrateLegacyMaturity below.
const LEGACY_STORAGE_KEY = "nist_csf_maturity_v1";

const FUNCTIONS = [
  {
    id: "GV",
    name: "Govern",
    description: "Establishes and monitors the organisation's cybersecurity risk management strategy, expectations, and policy.",
    categories: ["GV.OC — Organisational Context", "GV.RM — Risk Management Strategy", "GV.RR — Roles & Responsibilities", "GV.PO — Policy", "GV.OV — Oversight", "GV.SC — Supply Chain Risk"],
  },
  {
    id: "ID",
    name: "Identify",
    description: "Develops organisational understanding of managing cybersecurity risk to systems, people, assets, data, and capabilities.",
    categories: ["ID.AM — Asset Management", "ID.RA — Risk Assessment", "ID.IM — Improvement"],
  },
  {
    id: "PR",
    name: "Protect",
    description: "Develops and implements appropriate safeguards to ensure delivery of critical services.",
    categories: ["PR.AA — Identity Management & Access Control", "PR.AT — Awareness & Training", "PR.DS — Data Security", "PR.PS — Platform Security", "PR.IR — Technology Infrastructure Resilience"],
  },
  {
    id: "DE",
    name: "Detect",
    description: "Develops and implements appropriate activities to identify the occurrence of a cybersecurity event.",
    categories: ["DE.CM — Continuous Monitoring", "DE.AE — Adverse Event Analysis"],
  },
  {
    id: "RS",
    name: "Respond",
    description: "Develops and implements appropriate activities to take action regarding a detected cybersecurity incident.",
    categories: ["RS.MA — Incident Management", "RS.AN — Incident Analysis", "RS.CO — Incident Response Reporting", "RS.MI — Incident Mitigation"],
  },
  {
    id: "RC",
    name: "Recover",
    description: "Develops and implements appropriate activities to maintain plans for resilience and restore any capabilities or services impaired by a cybersecurity incident.",
    categories: ["RC.RP — Incident Recovery Plan Execution", "RC.CO — Incident Recovery Communication"],
  },
];
interface CheckConfig {
  checkKey: string;
  lastDoneAt: string | null;
  nextDueAt: string | null;
}

/** Whatever this browser still holds from before the assessment moved to the server. */
function legacyMaturity(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) ?? "{}"); }
  catch { return {}; }
}

function clearLegacyMaturity(): void {
  try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* private window — nothing to clear */ }
}

interface MaturityEntry { itemId: string; level: number; justification?: string | null }
interface MaturitySummary { average: number | null; assessed: number; total: number; target: number | null }
interface MaturityPayload {
  scale: { key: string; name: string; levels: Array<{ value: number; label: string; description: string }> };
  scales?: MaturityScaleOption[];
  entries: MaturityEntry[];
  target?: MaturityTargetRow | null;
  summary: MaturitySummary;
}

// ── Shared with the IWA 48 page (Iwa48Page.tsx): the maturity routes are the same for every
// framework assessed on a scale (backend routes/maturity.routes.ts, controls:write to write).

export interface MaturityScaleOption { key: string; name: string; levels: Array<{ value: number; label: string }> }
export interface MaturityTargetRow { scaleKey: string; targetLevel: number | null; targetDate: string | null }

/** Why an item sits at its level — the evidence an assessor asks for. Saved on its own. */
export function MaturityJustification({ frameworkId, itemId, value, canEdit, onSaved }: {
  frameworkId: string; itemId: string; value: string; canEdit: boolean; onSaved: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setDraft(value); }, [value]);
  if (!canEdit) return value ? <div style={{ ...text.hint, whiteSpace: "pre-wrap" }}>{value}</div> : <div style={{ ...text.hint, color: theme.color.faint }}>No justification recorded.</div>;
  async function save() {
    setSaving(true); setError("");
    try {
      await api.put(`/maturity/${frameworkId}/${itemId}`, { justification: draft.trim() || null });
      onSaved();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the justification.")); } finally { setSaving(false); }
  }
  const changed = draft.trim() !== value.trim();
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={4000}
        placeholder="What shows this level: the policy, process or record that evidences it"
        style={{ ...inputStyle, fontSize: text.hint.fontSize, resize: "vertical" }} aria-label="Justification" />
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" disabled={!changed || saving} onClick={() => void save()}
          style={{ ...btnSecondary, padding: "5px 12px", fontSize: text.hint.fontSize, opacity: !changed || saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save justification"}
        </button>
        {error && <span style={{ ...text.hint, color: theme.color.dangerText }}>{error}</span>}
      </div>
    </div>
  );
}

/** The scale in force, the level aimed at and by when (PUT /maturity/:frameworkId/settings/target). */
export function MaturityTargetSettings({ frameworkId, scales, scaleKey, target, levelWord, canEdit, onSaved }: {
  frameworkId: string; scales: MaturityScaleOption[]; scaleKey: string; target: MaturityTargetRow | null;
  levelWord: string; canEdit: boolean; onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [scale, setScale] = useState(scaleKey);
  const [level, setLevel] = useState(target?.targetLevel != null ? String(target.targetLevel) : "");
  const [date, setDate] = useState(toDateInput(target?.targetDate));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const levels = scales.find((s) => s.key === scale)?.levels ?? [];
  const current = scales.find((s) => s.key === scaleKey);
  const label = (v: number | null | undefined) => (v == null ? null : current?.levels.find((l) => l.value === v)?.label ?? String(v));

  function open() {
    setScale(scaleKey); setLevel(target?.targetLevel != null ? String(target.targetLevel) : "");
    setDate(toDateInput(target?.targetDate)); setError(""); setEditing(true);
  }
  async function save() {
    setSaving(true); setError("");
    try {
      await api.put(`/maturity/${frameworkId}/settings/target`, {
        scaleKey: scale,
        // A target on another scale cannot carry over, so changing the scale clears it unless one is chosen.
        targetLevel: level ? Number(level) : null,
        targetDate: dateInputToIso(date),
      });
      setEditing(false);
      onSaved();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the target.")); } finally { setSaving(false); }
  }

  if (!editing) {
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12, ...text.hint }}>
        <span>
          Target: {target?.targetLevel != null ? <strong style={{ color: theme.color.text }}>{levelWord} {target.targetLevel} — {label(target.targetLevel)}</strong> : "none set"}
          {target?.targetDate ? ` by ${fmtDateOnly(target.targetDate)}` : ""}
          {scales.length > 1 && current ? ` · scale: ${current.name}` : ""}
        </span>
        {canEdit && <button type="button" onClick={open} style={{ ...btnSecondary, padding: "4px 10px", fontSize: text.hint.fontSize }}>Set target</button>}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12, padding: 12, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md }}>
      {scales.length > 1 && (
        <label style={{ display: "grid", gap: 4, ...text.hint }}>
          Scale
          <select value={scale} onChange={(e) => { setScale(e.target.value); setLevel(""); }} style={{ ...inputStyle, fontSize: text.hint.fontSize }}>
            {scales.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </label>
      )}
      <label style={{ display: "grid", gap: 4, ...text.hint }}>
        Target {levelWord.toLowerCase()}
        <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ ...inputStyle, fontSize: text.hint.fontSize }}>
          <option value="">No target</option>
          {levels.map((l) => <option key={l.value} value={l.value}>{l.value} — {l.label}</option>)}
        </select>
      </label>
      <label style={{ display: "grid", gap: 4, ...text.hint }}>
        By
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inputStyle, fontSize: text.hint.fontSize }} />
      </label>
      <button type="button" disabled={saving} onClick={() => void save()} style={{ ...btnPrimary, padding: "6px 14px", fontSize: text.hint.fontSize, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
      <button type="button" onClick={() => setEditing(false)} style={{ ...btnSecondary, padding: "6px 14px", fontSize: text.hint.fontSize }}>Cancel</button>
      {error && <div style={{ ...text.hint, color: theme.color.dangerText, flexBasis: "100%" }}>{error}</div>}
    </div>
  );
}

/** "GV.OC — Organisational Context" -> reference and title. */
function splitCategory(cat: string): { reference: string; title: string } {
  const i = cat.indexOf(" — ");
  return i > 0 ? { reference: cat.slice(0, i), title: cat.slice(i + 3) } : { reference: "", title: cat };
}

export function NistCsfPage() {
  const accent = frameworkAccent("nist_csf");
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("controls:write");
  const [checks, setChecks] = useState<CheckConfig[]>([]);
  const [maturity, setMaturity] = useState<Record<string, number>>({});
  const [justifications, setJustifications] = useState<Record<string, string>>({});
  const [scaleInfo, setScaleInfo] = useState<{ key: string; scales: MaturityScaleOption[]; target: MaturityTargetRow | null } | null>(null);
  const [summary, setSummary] = useState<MaturitySummary | null>(null);
  const [levels, setLevels] = useState<MaturityLevel[]>(MATURITY_TIERS);
  // The CSF's own scale is Tiers, and it is explicit that they are not maturity levels;
  // any other scale (CMMI) is a maturity level.
  const word = !scaleInfo || scaleInfo.key === "nist_csf_tier" ? "Tier" : "Level";
  const [saveError, setSaveError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.get<{ checks: CheckConfig[] }>("/schedule/compliance-checks")
      .then((d) => setChecks(d.checks ?? []))
      .catch(() => {});
  }, []);

  const applyPayload = useCallback((d: MaturityPayload) => {
    setMaturity(Object.fromEntries(d.entries.filter((e) => e.level > 0).map((e) => [e.itemId, e.level])));
    setJustifications(Object.fromEntries(d.entries.map((e) => [e.itemId, e.justification ?? ""])));
    setScaleInfo({ key: d.scale.key, scales: d.scales ?? [d.scale], target: d.target ?? null });
    if (d.scale.levels?.length) setLevels(d.scale.levels);
    setSummary(d.summary);
  }, []);
  const reload = useCallback(() => { api.get<MaturityPayload>("/maturity/nist_csf").then(applyPayload).catch(() => {}); }, [applyPayload]);

  // Load the organisation's assessment, migrating anything this browser still holds from before
  // it moved to the server. The import only fills items nobody has answered, so a stale browser
  // can never overwrite what someone recorded elsewhere.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const legacy = legacyMaturity();
        if (Object.keys(legacy).length > 0) {
          await api.post("/maturity/nist_csf/import", { levels: legacy }).catch(() => {});
          clearLegacyMaturity();
        }
        const d = await api.get<MaturityPayload>("/maturity/nist_csf");
        if (!cancelled) applyPayload(d);
      } catch { /* the page still renders as a reference guide without the assessment */ }
    })();
    return () => { cancelled = true; };
  }, [applyPayload]);

  async function setFunctionMaturity(fnId: string, tier: number) {
    const previous = maturity;
    setMaturity((prev) => ({ ...prev, [fnId]: tier }));   // optimistic: the click should feel instant
    setSaveError("");
    try {
      await api.put(`/maturity/nist_csf/${fnId}`, { level: tier });
      const d = await api.get<MaturityPayload>("/maturity/nist_csf");
      applyPayload(d);
    } catch {
      setMaturity(previous);
      setSaveError(`Could not save that ${word.toLowerCase()}. Check your connection and try again.`);
    }
  }

  const nistChecks = checks.filter((c) => c.checkKey.startsWith("nist_csf:"));
  const completed  = nistChecks.filter((c) => c.lastDoneAt).length;

  // The server averages only the functions actually assessed — two at Tier 3 is Tier 3 on what
  // has been looked at, not Tier 1 overall — so its figure is preferred over a local one.
  const assessedCount = summary?.assessed ?? FUNCTIONS.filter((fn) => (maturity[fn.id] ?? 0) > 0).length;
  const avgMaturity   = summary?.average != null ? summary.average.toFixed(1) : "—";

  const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="nist_csf"
        page={STANDARD_PAGE_LABELS.referenceGuide}
        subtitle="The voluntary NIST framework for managing cyber security risk: self-assess the implementation tier of each of its six functions."
      />

      {/* Reference-only notice */}
      <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: theme.radius.md, background: theme.color.panelAlt, border: `1px solid ${theme.color.borderStrong}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Info size={16} style={{ flexShrink: 0, marginTop: 1, color: theme.color.heading }} />
        <div>
          <div style={{ ...text.body, fontWeight: 600, color: theme.color.heading, marginBottom: 3 }}>Reference framework, no certification workflow</div>
          <div style={text.hint}>
            CSF 2.0 (February 2024) adds the <strong>Govern</strong> function and expands supply-chain guidance. It is a guidance framework, not a certifiable
            standard: there is no formal assessment, Statement of Applicability, or certification audit. Use it as a maturity reference alongside your
            enrolled certifiable standards. It is free alongside any standard and can be switched on or off at any time in{" "}
            <PortalLink to="/my-services" style={{ color: theme.color.accentText }}>My Services</PortalLink>. Evidence collection activities appear in your{" "}
            <PortalLink to="/schedule" style={{ color: theme.color.accentText }}>Review Calendar</PortalLink>.
          </div>
        </div>
      </div>

      <SummaryBar
        label="Functions assessed"
        done={assessedCount}
        total={FUNCTIONS.length}
        cells={[
          { key: "avg", value: avgMaturity, label: `Average ${word.toLowerCase()}` },
          ...(summary?.target != null ? [{ key: "target", value: summary.target, label: `Target ${word.toLowerCase()}` }] : []),
          ...(nistChecks.length > 0 ? [{ key: "checks", value: `${completed}/${nistChecks.length}`, label: "Scheduled activities done" }] : []),
        ]}
      >
        {nistChecks.length > 0 && (
          <PortalLink to="/schedule" style={{ ...btnPrimary, alignSelf: "center", textDecoration: "none", fontSize: text.hint.fontSize, padding: "7px 14px" }}>
            View schedule
          </PortalLink>
        )}
      </SummaryBar>
      {saveError && <div style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{saveError}</div>}
      {scaleInfo && (
        <MaturityTargetSettings frameworkId="nist_csf" scales={scaleInfo.scales} scaleKey={scaleInfo.key} target={scaleInfo.target}
          levelWord={word} canEdit={canEdit} onSaved={reload} />
      )}

      <ControlGroupStack>
        {FUNCTIONS.map((fn) => {
          const tier = maturity[fn.id] ?? 0;
          const isOpen = expanded.has(fn.id);
          return (
            <ControlGroup
              key={fn.id}
              title={<><span style={{ ...text.ref, fontSize: "inherit", marginRight: 8 }}>{fn.id}</span>{fn.name}</>}
              hint={fn.description}
              accent={accent}
              count={fn.categories.length}
              noun="category"
              extra={<MaturityPill level={tier} levels={levels} />}
              expanded={isOpen}
              onToggle={() => toggle(fn.id)}
            >
              <div style={{ padding: "12px 14px", borderTop: `1px solid ${theme.color.border}`, display: "grid", gap: 10 }}>
                <MaturityPicker label={`Self-assessed ${word.toLowerCase()}`} level={tier} levels={levels} onPick={canEdit ? (v) => setFunctionMaturity(fn.id, v) : undefined} />
                <div>
                  <div style={{ ...text.label, marginBottom: 4 }}>Justification</div>
                  <MaturityJustification frameworkId="nist_csf" itemId={fn.id} value={justifications[fn.id] ?? ""} canEdit={canEdit} onSaved={reload} />
                </div>
              </div>
              {fn.categories.map((cat) => {
                const { reference, title } = splitCategory(cat);
                return <ReferenceRow key={cat} reference={reference} title={title} />;
              })}
            </ControlGroup>
          );
        })}
      </ControlGroupStack>

      <StandardNote title="Using NIST CSF with other standards">
        CSF maps well to ISO 27001 Annex A, the SOC 2 Trust Services Criteria and Cyber Essentials.
        The <PortalLink to="/control-mapping" style={{ color: theme.color.accentText }}>Standards overlap</PortalLink> page shows how your controls cross-reference across your enrolled standards,
        and CSF evidence collection activities appear in your <PortalLink to="/schedule" style={{ color: theme.color.accentText }}>Schedule</PortalLink>.
      </StandardNote>
    </div>
  );
}
